import * as React from 'react'

import type { ClientActivity } from '@/features/patients/hooks/useClientActivity'
import type { ClientHistory } from '@/features/patients/hooks/useClientHistory'
import type { PatientRecord } from '@/features/patients/types'
import { unansweredStreaks } from '@/features/patients/utils/clientActivity'
import { ClientCareGapsCard } from '../ClientCareGapsCard'
import { ClientCareTeamCard } from '../ClientCareTeamCard'
import { ClientContactCard } from '../ClientContactCard'
import { ClientDemographicsCard } from '../ClientDemographicsCard'
import { ClientSummaryTiles } from '../ClientSummaryTiles'

/**
 * Overview: where the client stands, how to reach them, what needs doing, who
 * cares for them, then the record itself. Ordered by how often each block is
 * the reason the sheet was opened.
 */
export function ClientOverviewTab({
  patient,
  history,
  activity,
  now,
}: {
  patient: PatientRecord
  history: ClientHistory
  activity: ClientActivity
  now: Date
}) {
  const unansweredByPhone = React.useMemo(
    () => unansweredStreaks(activity.calls.rows),
    [activity.calls.rows]
  )

  return (
    <div className="flex flex-col gap-4">
      <ClientSummaryTiles history={history} activity={activity} now={now} />
      <ClientContactCard
        patient={patient}
        unansweredByPhone={unansweredByPhone}
      />
      {/* Only when the API serves care gaps: an absent field is not "never
          evaluated", and rendering it as such would misstate every client.
          Keyed by client: ↑/↓ row navigation must land on the newest
          measurement year, not the year picked for the previous client. */}
      {patient.care_gaps !== undefined ? (
        <ClientCareGapsCard key={patient.id} careGaps={patient.care_gaps} />
      ) : null}
      <ClientCareTeamCard patient={patient} history={history} now={now} />
      <ClientDemographicsCard patient={patient} />
    </div>
  )
}
